import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, TrendingUp, TrendingDown, MessageCircle, Heart, Zap, Share2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import VentList from './VentList';
import ShareModal from './ShareModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsPage = ({ sessionId }) => {
  const navigate = useNavigate();
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [results, setResults] = useState(null);
  const [vents, setVents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [topicRes, resultsRes, ventsRes] = await Promise.all([
        axios.get(`${API}/topics/${topicId}`),
        axios.get(`${API}/topic/${topicId}/results`),
        axios.get(`${API}/vents/${topicId}`)
      ]);
      setTopic(topicRes.data);
      setResults(resultsRes.data);
      setVents(ventsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:results]', err?.message);
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-2 border-vent-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const forPercentage = results?.for_percentage || 0;
  const againstPercentage = results?.against_percentage || 0;
  const getWinner = () => {
    if (forPercentage > againstPercentage) return 'for';
    if (forPercentage < againstPercentage) return 'against';
    return 'tie';
  };
  const winner = getWinner();

  const getWinnerBgClass = () => {
    if (winner === 'for') return 'bg-vent-for/10';
    if (winner === 'against') return 'bg-vent-against/10';
    return '';
  };

  const getWinnerTextClass = () => {
    if (winner === 'for') return 'text-vent-for';
    if (winner === 'against') return 'text-vent-against';
    return 'text-zinc-400';
  };

  const getWinnerLabel = () => {
    if (winner === 'tie') return 'TIE';
    if (winner === 'for') return 'FOR WINS';
    return 'AGAINST WINS';
  };

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <h1 className="font-heading text-lg sm:text-xl font-bold text-white">Poll Results</h1>
          </div>
          <button
            onClick={() => setShowShareModal(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="share-results-btn"
          >
            <Share2 className="w-5 h-5 text-zinc-400 hover:text-vent-primary" />
          </button>
        </div>
      </nav>

      <main className="pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Topic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12"
          >
            <p className="text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-zinc-600 mb-3 sm:mb-4">
              {topic?.date && new Date(topic.date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 px-2" data-testid="results-topic-title">
              {topic?.title}
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto px-2">
              {topic?.description}
            </p>
          </motion.div>

          {/* Results Cards - Bento Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12"
          >
            {/* Total Votes */}
            <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 text-zinc-500 mb-2">
                <Users className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Total Votes</span>
              </div>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-white" data-testid="total-votes">
                {results?.total_votes || 0}
              </p>
            </div>

            {/* For Votes */}
            <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 border-l-4 border-vent-for">
              <div className="flex items-center gap-2 text-vent-for mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">For</span>
              </div>
              <p className="font-heading text-2xl sm:text-3xl font-bold text-vent-for" data-testid="for-votes">
                {results?.votes_for || 0}
              </p>
              <p className="text-zinc-500 text-sm">{forPercentage}%</p>
            </div>

            {/* Against Votes */}
            <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 border-l-4 border-vent-against">
              <div className="flex items-center gap-2 text-vent-against mb-2">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Against</span>
              </div>
              <p className="font-heading text-2xl sm:text-3xl font-bold text-vent-against" data-testid="against-votes">
                {results?.votes_against || 0}
              </p>
              <p className="text-zinc-500 text-sm">{againstPercentage}%</p>
            </div>

            {/* Winner */}
            <div className={`glass rounded-xl sm:rounded-2xl p-4 sm:p-6 ${getWinnerBgClass()}`}>
              <span className="text-xs uppercase tracking-wider text-zinc-500">Result</span>
              <p className={`font-heading text-xl sm:text-2xl font-bold mt-2 ${getWinnerTextClass()}`} data-testid="winner">
                {getWinnerLabel()}
              </p>
            </div>
          </motion.div>

          {/* Visual Bar */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-12"
          >
            <div className="h-8 bg-zinc-900 rounded-full overflow-hidden flex">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${forPercentage}%` }}
                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
                className="bg-gradient-to-r from-vent-for to-vent-for/70 flex items-center justify-end pr-4"
              >
                {forPercentage > 15 && (
                  <span className="text-black text-sm font-bold">{forPercentage}%</span>
                )}
              </motion.div>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${againstPercentage}%` }}
                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
                className="bg-gradient-to-r from-vent-against/70 to-vent-against flex items-center justify-start pl-4"
              >
                {againstPercentage > 15 && (
                  <span className="text-white text-sm font-bold">{againstPercentage}%</span>
                )}
              </motion.div>
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-vent-for font-bold">FOR</span>
              <span className="text-vent-against font-bold">AGAINST</span>
            </div>
          </motion.div>

          {/* Vents Section */}
          {vents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="font-heading text-xl font-bold text-zinc-300 mb-6 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-vent-primary" />
                Community Vents ({vents.length})
              </h3>
              <VentList vents={vents} sessionId={sessionId} onUpdate={fetchData} />
            </motion.div>
          )}

          {vents.length === 0 && (
            <div className="text-center text-zinc-600 py-12">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No vents yet for this topic</p>
            </div>
          )}
        </div>
      </main>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        topic={topic}
        results={results}
      />
    </div>
  );
};

export default ResultsPage;
